import React from 'react';
import { Checkbox, Typography } from '@mui/material';
import { ChevronRight, CalendarMonthOutlined } from '@mui/icons-material';
import clsx from 'clsx';

import './SchedulerRoutineItem.scss';

interface SchedulerRoutineItemProps {
  title: string;
  startTime: string;
  endTime: string;
  duration: string;
  checked?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onCheckChange?: (checked: boolean) => void;
  className?: string;
  selectable?: boolean;
  showCheckbox?: boolean;
  showScheduleIcon?: boolean;
}

const SchedulerRoutineItem: React.FC<SchedulerRoutineItemProps> = ({
  title,
  startTime,
  endTime,
  duration,
  checked = false,
  disabled = false,
  onPress = () => void 0,
  onCheckChange = () => void 0,
  className,
  selectable = true,
  showCheckbox = true,
  showScheduleIcon = true,
}) => {
  const handleCheckPress = (e: React.MouseEvent) => {
    if (!selectable) return;
    e.stopPropagation();
    onCheckChange(!checked);
  };

  return (
    <div
      className={clsx('scheduler-routine-item', className, {
        'scheduler-routine-item--disabled': !selectable || disabled,
        'scheduler-routine-item--checked': checked && selectable && !disabled,
      })}
      onClick={disabled ? undefined : onPress}
    >
      <div className="scheduler-routine-item__container">
        {/* Top row: icon and time */}
        <div className="scheduler-routine-item__top-row">
          <div className="scheduler-routine-item__left-content">
            {selectable ? (
              <div className="scheduler-routine-item__checkbox-container">
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onClick={handleCheckPress}
                  className="scheduler-routine-item__checkbox"
                  sx={{ padding: 0 }}
                />
                {showScheduleIcon && (
                  <CalendarMonthOutlined className="scheduler-routine-item__calendar-indicator" />
                )}
              </div>
            ) : (
              <CalendarMonthOutlined
                className={clsx('scheduler-routine-item__info-icon', {
                  'scheduler-routine-item__info-icon--calendar': true,
                })}
              />
            )}
          </div>
          {startTime && endTime && (
            <div className="scheduler-routine-item__time-section">
              <div className="scheduler-routine-item__time-pill">
                <Typography variant="caption" className="scheduler-routine-item__time-pill-text">
                  {startTime}
                </Typography>
              </div>
              <Typography variant="caption" className="scheduler-routine-item__time-separator">
                :
              </Typography>
              <div className="scheduler-routine-item__time-pill">
                <Typography variant="caption" className="scheduler-routine-item__time-pill-text">
                  {endTime}
                </Typography>
              </div>
            </div>
          )}
        </div>

        {/* Bottom row: title and duration */}
        <div className="scheduler-routine-item__bottom-row">
          <Typography variant="body1" className="scheduler-routine-item__title" title={title}>
            {title}
          </Typography>
          {duration && (
            <div className="scheduler-routine-item__duration-pill">
              <Typography variant="caption" className="scheduler-routine-item__duration-text">
                {duration}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <ChevronRight className="scheduler-routine-item__chevron" />
    </div>
  );
};

export default SchedulerRoutineItem;
